/**
 * Direct Cognito sign-in, so the console can render its own login screen.
 *
 * The Hosted UI is a page AWS renders, not us: its markup is fixed and only a
 * short allowlist of classes can be styled. That is enough to recolour it and
 * nowhere near enough to reproduce the OE console's two-column login. So the
 * console authenticates against the user pool itself and owns the screen.
 *
 * SRP, deliberately. `authenticateUser` uses SRP by default, which means the
 * password is never transmitted — only a zero-knowledge proof of it. The weaker
 * USER_PASSWORD_AUTH flow would have been less code, but enabling it on the pool
 * is a permanent downgrade for every client, present and future, in exchange for
 * saving one dependency. The pool stays SRP-only.
 */
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

import { authConfig, installSession, type Session } from './auth';

/** Cognito hands back a challenge before a first-time user has a real password. */
export type SignInOutcome =
  | { status: 'signed_in' }
  | { status: 'new_password_required' };

export class AuthError extends Error {
  readonly code: string;
  constructor(message: string, code = 'unknown') {
    super(message);
    this.name = 'AuthError';
    this.code = code;
  }
}

let pool: CognitoUserPool | null = null;

function userPool(): CognitoUserPool {
  if (!authConfig.userPoolId || !authConfig.clientId) {
    throw new AuthError(
      'This console has no user pool configured, so nobody can sign in.',
      'not_configured',
    );
  }
  if (!pool) {
    pool = new CognitoUserPool({
      UserPoolId: authConfig.userPoolId,
      ClientId: authConfig.clientId,
    });
  }
  return pool;
}

/** The user mid-challenge. Held between signIn() and completeNewPassword(). */
let pending: { user: CognitoUser; attributes: Record<string, string> } | null = null;

function toSession(raw: CognitoUserSession): Session {
  const access = raw.getAccessToken();
  const id = raw.getIdToken();
  const refresh = raw.getRefreshToken?.();
  return {
    access_token: access.getJwtToken(),
    id_token: id.getJwtToken(),
    refresh_token: refresh ? refresh.getToken() : undefined,
    token_type: 'Bearer',
    // getExpiration() is in seconds; the rest of the app works in epoch ms.
    expires_at: access.getExpiration() * 1000,
    obtained_at: Date.now(),
  };
}

/**
 * Cognito's messages are written for developers, not for the person locked out at
 * 9am. Translate the ones an operator can actually act on and pass the rest through.
 */
function friendly(err: unknown): AuthError {
  const e = err as { code?: string; name?: string; message?: string };
  const code = e?.code || e?.name || 'unknown';
  switch (code) {
    case 'NotAuthorizedException':
      return new AuthError('That email and password do not match.', code);
    case 'UserNotFoundException':
      // Deliberately the same wording as a wrong password: saying "no such user"
      // tells an attacker which addresses are real.
      return new AuthError('That email and password do not match.', code);
    case 'PasswordResetRequiredException':
      return new AuthError('Your password needs resetting. Use "Forgot password".', code);
    case 'UserNotConfirmedException':
      return new AuthError('This account has not been confirmed yet. Ask an admin.', code);
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return new AuthError('Too many attempts. Wait a minute and try again.', code);
    case 'InvalidPasswordException':
      return new AuthError(
        e?.message || 'That password does not meet the policy.',
        code,
      );
    case 'NetworkError':
      return new AuthError('Could not reach the sign-in service. Check your connection.', code);
    default:
      return new AuthError(e?.message || 'Sign-in failed.', code);
  }
}

/** Sign in with email + password over SRP. */
export function signIn(email: string, password: string): Promise<SignInOutcome> {
  const username = email.trim();
  const user = new CognitoUser({ Username: username, Pool: userPool() });
  const details = new AuthenticationDetails({ Username: username, Password: password });

  return new Promise<SignInOutcome>((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (session) => {
        pending = null;
        installSession(toSession(session));
        resolve({ status: 'signed_in' });
      },
      onFailure: (err) => {
        pending = null;
        reject(friendly(err));
      },
      newPasswordRequired: (attributes: Record<string, unknown>) => {
        // Cognito rejects these back on the challenge; they are read-only here.
        const attrs = { ...(attributes || {}) } as Record<string, string>;
        delete attrs.email_verified;
        delete attrs.email;
        delete attrs.phone_number_verified;
        pending = { user, attributes: attrs };
        resolve({ status: 'new_password_required' });
      },
    });
  });
}

/** Finish a first sign-in by setting a real password. */
export function completeNewPassword(newPassword: string): Promise<void> {
  if (!pending) {
    return Promise.reject(
      new AuthError('That sign-in attempt expired. Enter your email and password again.', 'expired'),
    );
  }
  const { user, attributes } = pending;
  return new Promise<void>((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, attributes, {
      onSuccess: (session) => {
        pending = null;
        installSession(toSession(session));
        resolve();
      },
      onFailure: (err) => reject(friendly(err)),
    });
  });
}

/** Start a self-service reset: Cognito emails a six-digit code. */
export function forgotPassword(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email.trim(), Pool: userPool() });
  return new Promise<void>((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      // Cognito resolves this path even for an unknown address, which is the
      // behaviour we want: it does not confirm whether an account exists.
      onFailure: (err) => reject(friendly(err)),
    });
  });
}

/** Finish a reset with the emailed code. */
export function confirmPassword(email: string, code: string, newPassword: string): Promise<void> {
  const user = new CognitoUser({ Username: email.trim(), Pool: userPool() });
  return new Promise<void>((resolve, reject) => {
    user.confirmPassword(code.trim(), newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(friendly(err)),
    });
  });
}

/** True while a sign-in is waiting on a new password. */
export function hasPendingChallenge(): boolean {
  return pending !== null;
}

/** Drop any half-finished challenge, e.g. when the user backs out of the form. */
export function clearPendingChallenge(): void {
  pending = null;
}
